// Phase 644 — Global Contact Directory.
// Search every apartment in every building by phone, owner/tenant name,
// apartment number, or building name — see `/api/contacts/search`.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import Layout from '../components/Layout';
import { Input } from '../components/ui/input';
import { Buildings as BuildingsIcon, User, Phone, Envelope, MagnifyingGlass, X as IconX } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const axCfg = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token') || ''}` } });

const HighlightedText = ({ text, query }) => {
  if (!text || !query || query.length < 2) return <>{text || ''}</>;
  const q = query.trim();
  const idx = (text || '').toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-100 text-zinc-900 px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

export default function Contacts() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    const raw = (q || '').trim();
    if (raw.length < 2) {
      setResults([]);
      setCount(0);
      setHasSearched(false);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/contacts/search`, { params: { q: raw, limit: 200 }, ...axCfg() });
      setResults(r.data.results || []);
      setCount(r.data.count || 0);
      setHasSearched(true);
    } catch (e) {
      setResults([]);
      setCount(0);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, runSearch]);

  const openContact = (r) => {
    if (r?.building_id) {
      navigate(`/buildings/${r.building_id}?focus_apt=${r.apartment_id || ''}`);
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-5 max-w-7xl mx-auto" data-testid="contacts-page">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">{t('contactDirectory') || 'Contact Directory'}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {t('contactsHint') || 'Find any owner or tenant across all buildings by phone, name, apartment, or building.'}
          </p>
        </div>

        {/* Search bar */}
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <Input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('contactsSearchPlaceholder') || 'Phone, owner / tenant name, apartment number, or building name…'}
            className="rounded-none h-11 pl-10 pr-10 text-sm"
            autoFocus
            data-testid="contacts-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              data-testid="contacts-search-clear"
              aria-label="Clear"
            >
              <IconX size={16} />
            </button>
          )}
        </div>

        {/* Results meta line */}
        <div className="text-xs text-zinc-500" data-testid="contacts-result-count">
          {loading
            ? (t('searching') || 'Searching…')
            : hasSearched
              ? `${count} ${count === 1 ? (t('result') || 'result') : (t('results') || 'results')}`
              : (t('contactsHintMin2') || 'Type at least 2 characters to search.')}
        </div>

        {/* Results table */}
        {results.length > 0 && (
          <div className="border border-zinc-200 bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">{t('building') || 'Building'}</th>
                  <th className="text-left px-3 py-2 w-24">{t('unit') || 'Unit'}</th>
                  <th className="text-left px-3 py-2">{t('owner') || 'Owner'}</th>
                  <th className="text-left px-3 py-2 w-36">{t('ownerPhone') || 'Owner Phone'}</th>
                  <th className="text-left px-3 py-2">{t('tenant') || 'Tenant'}</th>
                  <th className="text-left px-3 py-2 w-36">{t('tenantPhone') || 'Tenant Phone'}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr
                    key={r.apartment_id}
                    onClick={() => openContact(r)}
                    className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer align-top"
                    data-testid={`contacts-row-${r.apartment_id}`}
                  >
                    <td className="px-3 py-2 text-zinc-800">
                      <div className="flex items-start gap-2">
                        <BuildingsIcon size={14} className="text-indigo-500 mt-0.5 shrink-0" />
                        <span className="leading-snug"><HighlightedText text={r.building_name} query={query} /></span>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-zinc-700"><HighlightedText text={r.apartment_number} query={query} /></td>
                    <td className="px-3 py-2 text-zinc-700">
                      {r.owner_name ? (
                        <div className="flex items-start gap-2">
                          <User size={13} className="text-zinc-400 mt-0.5 shrink-0" />
                          <span className="leading-snug"><HighlightedText text={r.owner_name} query={query} /></span>
                        </div>
                      ) : <span className="text-zinc-300">—</span>}
                      {r.owner_email && (
                        <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 mt-1 ml-5">
                          <Envelope size={11} />
                          <span>{r.owner_email}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-zinc-700 whitespace-nowrap">
                      {r.owner_phone ? (
                        <a
                          href={`tel:${r.owner_phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-zinc-700 hover:text-indigo-700"
                        >
                          <Phone size={11} />
                          <HighlightedText text={r.owner_phone} query={query} />
                        </a>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-zinc-700">
                      {r.tenant_name ? (
                        <div className="flex items-start gap-2">
                          <User size={13} className="text-emerald-500 mt-0.5 shrink-0" />
                          <span className="leading-snug"><HighlightedText text={r.tenant_name} query={query} /></span>
                        </div>
                      ) : <span className="text-zinc-300">—</span>}
                      {r.tenant_email && (
                        <div className="flex items-center gap-1.5 text-[10px] text-zinc-400 mt-1 ml-5">
                          <Envelope size={11} />
                          <span>{r.tenant_email}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-zinc-700 whitespace-nowrap">
                      {r.tenant_phone ? (
                        <a
                          href={`tel:${r.tenant_phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1 text-zinc-700 hover:text-indigo-700"
                        >
                          <Phone size={11} />
                          <HighlightedText text={r.tenant_phone} query={query} />
                        </a>
                      ) : <span className="text-zinc-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && hasSearched && results.length === 0 && (
          <div className="border border-dashed border-zinc-200 bg-zinc-50/60 px-6 py-10 text-center text-sm text-zinc-500" data-testid="contacts-empty">
            {t('contactsNoMatch') || 'No matching contacts found.'}
          </div>
        )}
      </div>
    </Layout>
  );
}
